import axios, { AxiosPromise } from 'axios';

import { API } from 'consts';
import { GenericResponse } from 'types/GenericResponse';
import { CreateLibraryMeta } from 'types/store/CreateLibraryState';
import { DeleteLibraryMeta } from 'types/store/DeleteLibraryState';
import { TranscribeLibraryVideoMeta } from 'types/store/TranscribeLibraryVideo';
import { JobAddableVideoItem, ShareableAssetsResponse } from 'types/Library';
import { ShareableSmartGroupList } from 'types/SmartGroup';
import { ASSET_TYPE, ShareableContentSource } from 'types/store/AssetLibrary';

import { uploadFile } from './upload';

const BASE_URL = `${API.URL}/${API.VERSION}`;
const LIBRARY_URL = `${BASE_URL}/library`;

export const createLibrary = async (
  data: CreateLibraryMeta,
  token: string,
): Promise<any> => {
  // const fd = new FormData();

  try {
    if (data.asset_upload) {
      const fileLink = await uploadFile({
        file: data.asset_upload,
        token,
        folder: 'library',
      });

      delete data.asset_upload;
      data.file_link = fileLink;
    } else {
      data.file_link = data.asset_url;
      delete data.asset_url;
    }

    //mutate the db
    // fd.append('data', JSON.stringify(data));
    const payload = {
      ...data,
    };
    return axios({
      method: 'POST',
      url: `${LIBRARY_URL}/create`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
      data: payload,
    });
  } catch (err: any) {
    console.log(err);
    throw new Error(err);
  }
};

export const updateLibrary = async (
  data: CreateLibraryMeta,
  token: string,
): Promise<any> => {
  try {
    if (data?.asset_upload) {
      const fileLink = await uploadFile({
        file: data.asset_upload,
        token,
        folder: 'library',
      });

      delete data.asset_upload;
      data.file_link = fileLink;
    } else {
      data.file_link = data.asset_url;
      delete data.asset_url;
    }

    const payload = {
      ...data,
    };
    return axios({
      method: 'POST',
      url: `${LIBRARY_URL}/update`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
      data: payload,
    });
  } catch (err: any) {
    console.log(err);
    throw new Error(err);
  }
};

export const deleteLibrary = (
  data: DeleteLibraryMeta,
  token: string,
): AxiosPromise<GenericResponse> => {
  return axios({
    method: 'POST',
    url: `${LIBRARY_URL}/deleteById`,
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: data,
  });
};

export const getLibraryAssets = (
  token: string,
): AxiosPromise<GenericResponse> => {
  return axios({
    method: 'GET',
    url: `${LIBRARY_URL}/getLibraryAssets`,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
};

export const getTags = (token: string): any => {
  return axios({
    method: 'get',
    url: `${LIBRARY_URL}/getTags`,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
};

export const transcribeLibraryVideo = (
  data: TranscribeLibraryVideoMeta,
  token: string,
): any => {
  return axios({
    method: 'post',
    url: `${LIBRARY_URL}/transcribeLibraryVideo`,
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data,
  });
};

export const updateThumbnail = (
  data: {
    story_id?: string;
    library_id?: string | null;
    story_approval_version_file_id?: string;
    thumbnail_image: string;
    story_video_id?: string;
  },
  folder: 'library' | 'story',
  token: string,
): AxiosPromise<GenericResponse> => {
  return axios({
    method: 'post',
    url: `${BASE_URL}/${folder}/updateThumbnail`,
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data,
  });
};

export const getListJobAddableVideos = ({
  offset,
  limit,
  token,
  search = '',
}: {
  offset: number;
  limit: number;
  token: string;
  search: string;
}): AxiosPromise<GenericResponse<JobAddableVideoItem[]>> => {
  return axios({
    method: 'get',
    url: `${LIBRARY_URL}/job-addable-videos?offset=${
      offset || 0
    }&limit=${limit}&search=${search}`,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
};

export const getListSmartGroups = ({
  type,
  storyId,
  token,
}: {
  type: string;
  storyId: string;
  token: string;
}): AxiosPromise<GenericResponse<ShareableSmartGroupList>> => {
  return axios({
    method: 'get',
    url: `${BASE_URL}/content/${type}/${storyId}/smart-groups`,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
};

export const getAssetsBySmartGroup = ({
  type,
  storyId,
  smartGroupId,
  shareRuleConfigured,
  token,
}: {
  type: string;
  storyId: string;
  smartGroupId: string;
  shareRuleConfigured: boolean;
  token: string;
}): AxiosPromise<GenericResponse<ShareableAssetsResponse>> => {
  return axios({
    method: 'get',
    url: `${BASE_URL}/content/${type}/${storyId}/shareable-assets?smartGroupId=${smartGroupId}&shareRuleConfigured=${shareRuleConfigured}`,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
};

export const createRuleForSmartGroup = (
  data: {
    contentType: string;
    contentId: string;
    smartGroupId: string;
    assetSource: ShareableContentSource;
    assetType: ASSET_TYPE;
    assetRef: string;
    socialCopy: string;
    isActive: boolean;
  },
  token: string,
): AxiosPromise<GenericResponse> => {
  return axios({
    method: 'post',
    url: `${BASE_URL}/smart-group-share-rules`,
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data,
  });
};

export const updateRuleForSmartGroup = (
  data: {
    socialCopy: string;
    isActive: boolean;
  },
  shareRuleId: string,
  token: string,
): AxiosPromise<GenericResponse> => {
  return axios({
    method: 'patch',
    url: `${BASE_URL}/smart-group-share-rules/${shareRuleId}`,
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data,
  });
};
